import { useJobillaOrgId } from '@/hooks'
import { useAuth } from '@/lib'
import { cn } from '@/lib/utils'
import { roleEnum, useWhatsAppAiAgentConfigConsentStatus } from '@jobilla/nexus-sdk'
import { getRouteApi, Link } from '@tanstack/react-router'
import { AlertTriangleIcon, XIcon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

const companyRoute = getRouteApi('/_auth/company/$companyId')

function isStoredInSession(key: string): boolean {
	try {
		return sessionStorage.getItem(key) !== null
	} catch {
		return false
	}
}

function storeInSession(key: string): void {
	try {
		sessionStorage.setItem(key, '1')
	} catch {
		// storage unavailable
	}
}

export function WhatsAppAiConsentAlert({ className }: { className?: string }) {
	const { t, i18n } = useTranslation('common')
	const organisationId = useJobillaOrgId()
	const { companyId } = companyRoute.useParams()
	const { role } = useAuth()
	const [dismissedKey, setDismissedKey] = useState<string | null>(null)

	const { data } = useWhatsAppAiAgentConfigConsentStatus(organisationId, {
		enabled: !!organisationId,
		staleTime: 5 * 60 * 1000,
	})

	if (!organisationId || !data?.consentRequired) { return null }

	const stopsAt = data.agentStopsAt ? new Date(data.agentStopsAt) : null
	const stopped = stopsAt === null || stopsAt.getTime() <= Date.now()
	const dismissalKey = `whatsapp-ai-consent-dismissed:${organisationId}:${stopped ? 'stopped' : 'grace'}`

	if (dismissalKey === dismissedKey || isStoredInSession(dismissalKey)) { return null }

	const canChangeSetting = role === roleEnum.admin || role === roleEnum.jobillan
	const message = stopped
		? t('dashboard.whatsappAiConsent.stopped')
		: t('dashboard.whatsappAiConsent.warning', {
			date: stopsAt.toLocaleDateString(i18n.language, { year: 'numeric', month: 'long', day: 'numeric' }),
		})

	const dismiss = () => {
		storeInSession(dismissalKey)
		setDismissedKey(dismissalKey)
	}

	return (
		<div
			role="alert"
			className={cn(
				'flex items-center gap-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3',
				className,
			)}
		>
			<AlertTriangleIcon className="size-5 shrink-0 text-amber-600" aria-hidden />
			<div className="flex-1 text-sm text-amber-900">
				{message} {canChangeSetting ? null : t('dashboard.whatsappAiConsent.askAdmin')}
			</div>
			{canChangeSetting
				? (
					<Link
						to="/company/$companyId/settings/ai"
						params={{ companyId }}
						className="shrink-0 rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700 transition-colors"
					>
						{t('dashboard.whatsappAiConsent.cta')}
					</Link>
				)
				: null}
			<button
				type="button"
				onClick={dismiss}
				aria-label={t('common.dismiss')}
				className="shrink-0 rounded-md p-1 text-amber-700 hover:bg-amber-100 transition-colors"
			>
				<XIcon className="size-4" />
			</button>
		</div>
	)
}
